import ApiService from '@/service'
import { RequestTemplate, RuleHelperDict, RuleInputDict, ScriptKeyDetails, ScriptKeysLookup } from '@/types'
import { moduleActionContext } from '@/vuex'
import { defineActions, defineGetters, defineModule, defineMutations } from 'direct-vuex'

export const helpersActionContext = (context: any) => moduleActionContext(context, helpersModule)

interface ScriptKeyState {
  inputs?: RuleInputDict
  helpers?: RuleHelperDict
  isLoading: boolean
  requests: RequestTemplate[]
  scriptKeyDetails: ScriptKeyDetails | undefined
}

const helpersModule = defineModule({
  namespaced: true,

  state: {
    inputs: undefined,
    helpers: undefined,
    isLoading: false,
    requests: [],
    scriptKeyDetails: undefined,
  } as ScriptKeyState,

  getters: defineGetters<ScriptKeyState>()({
    validHelpers(state: ScriptKeyState) {
      return Object.keys(state.helpers || {}!)
    },

    validInputs(state: ScriptKeyState) {
      return Object.keys(state.inputs || {})
    },

    scriptKeysLookup(state: ScriptKeyState): ScriptKeysLookup {
      return {
        inputs: state.inputs!,
        helpers: state.helpers!,
      }
    },

    deprecatedKeys(state: ScriptKeyState) {
      return {
        deprecatedInputs: Object.values(state.inputs!).filter(v => Object.hasOwn(v, 'deprecated')),
        deprecatedHelpers: Object.entries(state.helpers!)
          .filter(v => v[1].deprecated === true)
          .map(el => el[0]),
      }
    },

    validUserFunctionsNames(state: ScriptKeyState) {
      return Object.entries(state.helpers!)
        .map(([label, body]) => ({
          label,
          body,
        }))
        .filter(el => el.body.source_type === 'iris')
        .map(el => el.label)
    },
  }),

  mutations: defineMutations<ScriptKeyState>()({
    // This is for unit test usage, so one can setup test state without being mega-intrusive
    REPLACE(state: ScriptKeyState, payload: Partial<ScriptKeyState>) {
      const keys = Object.keys(payload) as (keyof ScriptKeyState)[]

      keys.forEach((key: keyof ScriptKeyState) => {
        state[key] = payload[key] as never
      })
    },

    SET_LOADING(state: ScriptKeyState, isLoading: boolean) {
      state.isLoading = isLoading
    },

    SET_RULE_INPUTS(state: ScriptKeyState, inputs: RuleInputDict) {
      state.inputs = inputs
    },

    SET_RULE_HELPERS(state: ScriptKeyState, helpers: RuleHelperDict) {
      state.helpers = helpers
    },

    SET_SCRIPT_KEY_DETAILS(state: ScriptKeyState, payload?: ScriptKeyDetails) {
      state.scriptKeyDetails = payload
    },
  }),

  actions: defineActions({
    async GET_INPUTS_METADATA(ctx): Promise<RuleInputDict> {
      const { commit } = helpersActionContext(ctx)
      try {
        const { data: inputs } = await ApiService.getInputs()
        commit.SET_RULE_INPUTS(inputs)
        return inputs as RuleInputDict
      } finally {
        commit.SET_LOADING(false)
      }
    },

    async GET_HELPERS_METADATA(ctx): Promise<RuleHelperDict> {
      const { commit } = helpersActionContext(ctx)
      try {
        const helpers = await ApiService.getHelpers()
        commit.SET_RULE_HELPERS(helpers)
        return helpers
      } finally {
        commit.SET_LOADING(false)
      }
    },

    async VIEW_SCRIPT_KEY_DETAILS(ctx, variable?: ScriptKeyDetails): Promise<ScriptKeyDetails | undefined> {
      const { commit } = helpersActionContext(ctx)

      commit.SET_SCRIPT_KEY_DETAILS(variable)
      return variable
    },
  }),
})

export default helpersModule
